import { useEffect, useRef, useState } from "react";
import { FileText, Image as ImageIcon, Mic, Video, Download, X, File, Sheet, FileType, ChevronLeft, ChevronRight } from "lucide-react";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { TransformWrapper, TransformComponent } from "react-zoom-pan-pinch";

export const kindOf = (m) => {
  const mime = m?.media?.mime || "";
  if (m?.type === "image" || m?.type === "sticker" || mime.startsWith("image/")) return "image";
  if (m?.type === "video" || mime.startsWith("video/")) return "video";
  if (m?.type === "audio" || mime.startsWith("audio/")) return "audio";
  return "document";
};
const downloadUrl = (url) => (url ? `${url}${url.includes("?") ? "&" : "?"}download=1` : "#");
const fmtSize = (n) => (n ? (n > 1048576 ? `${(n / 1048576).toFixed(1)} MB` : `${Math.round(n / 1024)} KB`) : "");
const docIcon = (mime = "", name = "") => {
  const s = `${mime} ${name}`.toLowerCase();
  if (s.includes("pdf")) return { Icon: FileText, color: "#E02424", label: "PDF" };
  if (s.includes("sheet") || s.includes("excel") || /\.xlsx?$|\.csv$/.test(s)) return { Icon: Sheet, color: "#0B7C5A", label: "Sheet" };
  if (s.includes("word") || /\.docx?$/.test(s)) return { Icon: FileType, color: "#2563EB", label: "Doc" };
  return { Icon: File, color: "#6B7189", label: "File" };
};

// Full-screen lightbox: pinch/scroll zoom, swipe or arrow keys between images of the same gallery.
function Lightbox({ open, onClose, images, start }) {
  const [i, setI] = useState(start);
  const touch = useRef(null);
  useEffect(() => { if (open) setI(start); }, [open, start]);
  const img = images[i];
  const go = (d) => setI((x) => Math.min(images.length - 1, Math.max(0, x + d)));
  const dl = img && (
    <a href={downloadUrl(img.url)} download={img.filename} title="Download" className="inline-flex items-center justify-center w-10 h-10 rounded-full bg-white/15 hover:bg-white/25 text-white" data-testid="lightbox-download"><Download className="w-4 h-4" /></a>);
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-4xl p-0 bg-black border-0 [&>button]:hidden max-sm:pt-0 max-sm:pb-0 overflow-hidden" data-testid="att-lightbox"
        onKeyDown={(e) => { if (e.key === "ArrowLeft") go(-1); if (e.key === "ArrowRight") go(1); }}>
        <DialogTitle className="sr-only">{img?.filename || "Image preview"}</DialogTitle>
        <div className="relative h-[100dvh] sm:h-[80vh] flex flex-col"
          onTouchStart={(e) => { if (e.touches.length === 1) touch.current = e.touches[0].clientX; }}
          onTouchEnd={(e) => { if (touch.current == null) return; const dx = e.changedTouches[0].clientX - touch.current; touch.current = null; if (Math.abs(dx) > 60) go(dx < 0 ? 1 : -1); }}>
          <div className="flex-1 min-h-0">
            {img && <TransformWrapper key={img.url} minScale={1} maxScale={5} doubleClick={{ mode: "toggle" }} panning={{ velocityDisabled: true }}>
              <TransformComponent wrapperClass="!w-full !h-full" contentClass="!w-full !h-full flex items-center justify-center">
                <img src={img.url} alt="" className="max-h-full max-w-full object-contain select-none" draggable={false} data-testid="lightbox-image" />
              </TransformComponent>
            </TransformWrapper>}
          </div>
          {images.length > 1 && <>
            <button onClick={() => go(-1)} disabled={i === 0} className="absolute left-2 top-1/2 -translate-y-1/2 w-11 h-11 rounded-full bg-white/15 text-white flex items-center justify-center disabled:opacity-30" data-testid="lightbox-prev"><ChevronLeft className="w-5 h-5" /></button>
            <button onClick={() => go(1)} disabled={i === images.length - 1} className="absolute right-2 top-1/2 -translate-y-1/2 w-11 h-11 rounded-full bg-white/15 text-white flex items-center justify-center disabled:opacity-30" data-testid="lightbox-next"><ChevronRight className="w-5 h-5" /></button>
          </>}
          <div className="flex items-center justify-between text-white/80 text-xs px-3 py-2 pb-safe bg-black/60">
            <span className="truncate">{images.length > 1 && <span className="mr-2 font-semibold" data-testid="lightbox-counter">{i + 1}/{images.length}</span>}{img?.filename} {fmtSize(img?.size) && `· ${fmtSize(img?.size)}`}<span className="hidden sm:inline text-white/50"> · pinch or scroll to zoom</span></span>
            <div className="flex items-center gap-2 shrink-0">{dl}<button onClick={onClose} className="w-10 h-10 rounded-full bg-white/15 inline-flex items-center justify-center" data-testid="lightbox-close"><X className="w-4 h-4" /></button></div>
          </div>
        </div>
      </DialogContent>
    </Dialog>);
}

const DownloadBtn = ({ media, dark, id }) => (
  <a href={downloadUrl(media.url)} download={media.filename} onClick={(e) => e.stopPropagation()} title="Download"
    className={`inline-flex items-center justify-center w-8 h-8 rounded-full ${dark ? "bg-white/15 hover:bg-white/25 text-white" : "bg-black/50 hover:bg-black/70 text-white"}`} data-testid={`att-download-${id}`}>
    <Download className="w-4 h-4" /></a>);

function MissingAttachment({ m, kind, sub }) {
  const Icon = { image: ImageIcon, video: Video, audio: Mic, document: FileText }[kind];
  return <div className={`flex items-center gap-2 text-sm ${sub}`} data-testid={`att-missing-${m.id}`}><Icon className="w-4 h-4" /> {m.media?.filename || kind} · not downloaded</div>;
}

function ImageAttachment({ m, media, compact, dark, gallery, size }) {
  const [open, setOpen] = useState(false);
  const images = (gallery || []).map((a) => ({ id: a.message_id || a.id, type: a.type, media: a.media })).filter((a) => kindOf(a) === "image" && a.media?.url).map((a) => ({ id: a.id, ...a.media }));
  const list = images.length ? images : [{ id: m.id, ...media }];
  const start = Math.max(0, list.findIndex((x) => x.id === m.id));
  return (
    <>
      <div className={`relative group mb-1 ${compact ? "block sm:inline-block" : ""}`} data-testid={`att-image-${m.id}`}>
        <img src={media.url} alt={media.filename || "image"} loading="lazy" onClick={(e) => { e.stopPropagation(); setOpen(true); }}
          className={`${size} object-cover rounded-lg cursor-zoom-in bg-black/5`} />
        <div className="absolute top-1.5 right-1.5 opacity-0 group-hover:opacity-100 transition-opacity"><DownloadBtn media={media} dark={dark} id={m.id} /></div>
      </div>
      <Lightbox open={open} onClose={() => setOpen(false)} images={list} start={start} />
    </>);
}

const VideoAttachment = ({ m, media, compact, dark }) => (
  <div className={`relative mb-1 ${compact ? "w-full sm:w-40" : ""}`} data-testid={`att-video-${m.id}`} onClick={(e) => e.stopPropagation()}>
    <video src={media.url} controls preload="metadata" className={`${compact ? "w-full aspect-square sm:w-40 sm:h-24 sm:aspect-auto" : "max-h-64 w-full"} rounded-lg bg-black object-cover`} />
    <div className="absolute top-1.5 right-1.5"><DownloadBtn media={media} dark={dark} id={m.id} /></div>
  </div>);

const AudioAttachment = ({ m, media, dark, sub }) => (
  <div className="flex items-center gap-2 mb-1 max-w-full" data-testid={`att-audio-${m.id}`} onClick={(e) => e.stopPropagation()}>
    <Mic className={`w-4 h-4 ${sub}`} /><audio src={media.url} controls preload="metadata" className="h-9 max-w-[220px] min-w-0" /><DownloadBtn media={media} dark={dark} id={m.id} />
  </div>);

function DocumentAttachment({ m, media, compact, dark, sub }) {
  const { Icon, color, label } = docIcon(media.mime, media.filename);
  const openDoc = () => window.open(media.url, "_blank", "noopener");
  return (
    <div role="link" tabIndex={0} onClick={(e) => { e.stopPropagation(); openDoc(); }} onKeyDown={(e) => e.key === "Enter" && openDoc()} data-testid={`att-doc-${m.id}`}
      className={`flex items-center gap-3 rounded-lg px-3 py-2 mb-1 text-sm cursor-pointer max-w-full ${dark ? "bg-white/10 hover:bg-white/15" : "bg-[#F5F6FA] hover:bg-[#EEF0F6] border border-[#E6E8F0]"} ${compact ? "w-full sm:w-56" : "min-w-0 sm:min-w-[220px]"}`}>
      <div className="w-9 h-9 rounded-md flex items-center justify-center shrink-0" style={{ background: `${color}22`, color }}><Icon className="w-5 h-5" /></div>
      <div className="min-w-0 flex-1"><div className="truncate font-medium">{media.filename || label}</div><div className={`text-[11px] ${sub}`}>{label}{fmtSize(media.size) && ` · ${fmtSize(media.size)}`} · tap to open</div></div>
      <DownloadBtn media={media} dark={dark} id={m.id} />
    </div>);
}

const RENDERERS = { image: ImageAttachment, video: VideoAttachment, audio: AudioAttachment, document: DocumentAttachment };

// Renders a WhatsApp attachment (image / video / audio / document) with preview + download. `dark` for outbound bubbles.
// `gallery` (optional): sibling attachments so the lightbox can swipe between images.
export function Attachment({ m, dark = false, compact = false, gallery }) {
  const media = m.media || {};
  const kind = kindOf(m);
  const sub = dark ? "text-white/70" : "text-[#6B7189]";
  if (!media.url) return <MissingAttachment m={m} kind={kind} sub={sub} />;
  const size = compact ? "w-full aspect-square sm:w-24 sm:h-24 sm:aspect-auto" : "max-h-64 w-full min-w-[180px]";
  const Renderer = RENDERERS[kind];
  return <Renderer m={m} media={media} compact={compact} dark={dark} sub={sub} gallery={gallery} size={size} />;
}
